import React, { useMemo, useRef, useState } from "react";
import { HbrIcon, HbrSpinner } from "@harbor/elements-react-standalone";
import { arrowSquareOut, statusInfoSquare, check, x } from "@harbor/elements/icons";
import StbButton from "../security-toolbox-ui/StbButton";

type UploadStatus = "queued" | "uploading" | "success" | "error";

type UploadRow = {
  id: string;
  file: File;
  status: UploadStatus;
  progress?: number; // 0–100, populated when the upload hook reports progress via onProgress
  error?: string;
};

export type FileDropperProps = {
  title?: string;
  description?: string;

  // Tag to apply to uploaded docs (e.g., kb.kb_id)
  kbTag?: string;

  // Optional upload meta
  isPublic?: boolean;
  allowedRoles?: string;

  // Constraints
  maxSizeBytes?: number;              // default 500MB
  allowedExtensions?: string[];       // e.g. ["pdf","docx","pptx"]
  accept?: string;                    // input accept attribute, e.g. ".pdf,.docx"

  // Hook function you pass in (keeps component generic). `onProgress` reports upload
  // progress (0–100); a hook that cannot report it simply never calls it, and the row falls
  // back to an indeterminate spinner.
  upload: (payload: {
    file: File;
    title?: string;
    description?: string;
    is_public?: boolean;
    allowed_roles?: string;
    tags?: string;
    onProgress?: (percent: number) => void;
  }) => Promise<any>;

  // Callback after a file succeeds
  onUploaded?: (result: any) => void;

  // Callback after all uploads finish
  onAllComplete?: () => void;

  // UI options
  disabled?: boolean;
};

const DEFAULT_MAX = 500 * 1024 * 1024; // 500MB

const getExt = (name: string) => {
  const parts = name.split(".");
  return (parts.length > 1 ? parts[parts.length - 1] : "").toLowerCase();
};

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileDropper: React.FC<FileDropperProps> = ({
                                                   title = "Upload documents",
                                                   description = "Drag & drop files here, or click to select files.",
                                                   kbTag,
                                                   isPublic = false,
                                                   allowedRoles,
                                                   maxSizeBytes = DEFAULT_MAX,
                                                   allowedExtensions,
                                                   accept,
                                                   upload,
                                                   onUploaded,
                                                   onAllComplete,
                                                   disabled = false,
                                                 }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [rows, setRows] = useState<UploadRow[]>([]);
  const [busy, setBusy] = useState(false);

  const allowedExtSet = useMemo(() => {
    if (!allowedExtensions || allowedExtensions.length === 0) return null;
    return new Set(allowedExtensions.map((e) => e.toLowerCase().replace(/^\./, "")));
  }, [allowedExtensions]);

  const validateFile = (file: File): string | null => {
    if (file.size > maxSizeBytes) {
      return `File too large (${formatBytes(file.size)}). Max is ${formatBytes(maxSizeBytes)}.`;
    }
    if (allowedExtSet) {
      const ext = getExt(file.name);
      if (!allowedExtSet.has(ext)) {
        return `Unsupported file type .${ext}. Allowed: ${Array.from(allowedExtSet).join(", ")}.`;
      }
    }
    return null;
  };

  const addFiles = (files: FileList | File[]) => {
    const list = Array.from(files);
    const newRows: UploadRow[] = list.map((file) => ({
      id: `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      file,
      status: "queued",
    }));

    // Mark invalid files immediately
    for (const r of newRows) {
      const err = validateFile(r.file);
      if (err) {
        r.status = "error";
        r.error = err;
      }
    }

    setRows((prev) => [...newRows, ...prev]);
  };

  const onPickFiles = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    addFiles(e.target.files);
    // reset input so selecting same file twice still triggers change
    e.target.value = "";
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (disabled) return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };

  const uploadAll = async () => {
    const pending = rows.filter((r) => r.status === "queued");
    if (pending.length === 0) return;

    setBusy(true);
    try {
      for (const r of pending) {
        setRows((prev) =>
          prev.map((x) =>
            x.id === r.id ? { ...x, status: "uploading", progress: 0, error: undefined } : x
          )
        );

        try {
          const res = await upload({
            file: r.file,
            title: r.file.name,
            description: undefined,
            is_public: isPublic,
            allowed_roles: allowedRoles,
            tags: kbTag,
            onProgress: (percent) =>
              setRows((prev) =>
                prev.map((x) => (x.id === r.id ? { ...x, progress: percent } : x))
              ),
          });

          setRows((prev) =>
            prev.map((x) => (x.id === r.id ? { ...x, status: "success" } : x))
          );
          onUploaded?.(res);
        } catch (err: any) {
          const msg = err?.message || "Upload failed";
          setRows((prev) =>
            prev.map((x) => (x.id === r.id ? { ...x, status: "error", error: msg } : x))
          );
        }
      }
    } finally {
      setBusy(false);
      onAllComplete?.();
    }
  };

  const clear = () => setRows([]);

  const removeRow = (id: string) => setRows((prev) => prev.filter((r) => r.id !== id));

  const hasQueued = rows.some((r) => r.status === "queued");
  const hasAny = rows.length > 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="m-0 text-base">{title}</h3>
          <div className="text-xs text-gray-600">{description}</div>
          {kbTag && (
            <div className="text-xs text-gray-500 mt-1">
              Tagging uploads with KB: <b>{kbTag}</b>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <StbButton role="add" onClick={() => onPickFiles()} disabled={disabled || busy}>
            Add files
          </StbButton>
          <StbButton role="save" onClick={() => uploadAll()} disabled={disabled || busy || !hasQueued}>
            Upload
          </StbButton>
          <StbButton role="cancel" onClick={() => clear()} disabled={disabled || busy || !hasAny}>
            Clear
          </StbButton>
        </div>
      </div>

      <div
        onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); if (!disabled) setDragOver(true); }}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
        onDrop={onDrop}
        onClick={onPickFiles}
        className={`border rounded p-6 cursor-pointer transition-colors bg-white ${
          disabled ? "opacity-60 cursor-not-allowed" : ""
        } ${dragOver ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-blue-400"}`}
        role="button"
        tabIndex={0}
      >
        <div className="flex items-center gap-3">
          <HbrIcon asset={arrowSquareOut} />
          <div>
            <div className="font-medium text-sm">Drop files to upload</div>
            <div className="text-xs text-gray-600">
              {accept ? `Accepted: ${accept}` : "Accepted: pdf, docx, pptx, xlsx, mp4, mp3, wav, json, jsonl, txt, md, csv"}
            </div>
            <div className="text-xs text-gray-500">Max size: {formatBytes(maxSizeBytes)}</div>
          </div>
          {busy && (
            <div className="ml-auto flex items-center gap-2 text-xs text-gray-600">
              <HbrSpinner size="small" />
              Uploading...
            </div>
          )}
        </div>

        <input
          ref={inputRef}
          type="file"
          multiple
          accept={accept}
          style={{ display: "none" }}
          onChange={onInputChange}
          disabled={disabled}
        />
      </div>

      {rows.length > 0 && (
        <div className="border rounded bg-white overflow-hidden">
          <div className="p-2 text-xs text-gray-600 flex items-center gap-2 border-b">
            <HbrIcon asset={statusInfoSquare} sentiment="info" />
            Upload queue
          </div>

          <div className="max-h-64 overflow-auto">
            {rows.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 p-2 border-b last:border-b-0">
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{r.file.name}</div>
                  <div className="text-xs text-gray-500">{formatBytes(r.file.size)}</div>
                  {r.status === "uploading" && typeof r.progress === "number" && (
                    <div className="mt-1">
                      <div className="h-1.5 w-full rounded bg-gray-200 overflow-hidden">
                        <div
                          className="h-full bg-blue-500 transition-all"
                          style={{ width: `${r.progress}%` }}
                        />
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">Uploading… {r.progress}%</div>
                    </div>
                  )}
                  {r.error && <div className="text-xs text-red-600 mt-1">{r.error}</div>}
                </div>

                <div className="flex items-center gap-2">
                  {r.status === "uploading" && typeof r.progress !== "number" && (
                    <HbrSpinner size="small" />
                  )}
                  {r.status === "success" && <HbrIcon asset={check} sentiment="success" />}
                  {r.status === "error" && <HbrIcon asset={x} sentiment="danger" />}

                  <StbButton
                    role="delete"
                    iconOnly
                    onClick={() => removeRow(r.id)}
                    disabled={busy || r.status === "uploading"}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileDropper;